import { Link, useNavigate } from "react-router-dom";
import {
  Breadcrumb,
  Card,
  Form,
  Row,
  Col,
  FormGroup,
  Button,
  Container
} from "react-bootstrap";
import {
  Page,
  Spinner,
  IconButton,
  IconButtonType,
  Divider,
  ErrorMessages
} from "@whitespace/whitespace-ui";
import { ariaInvalidCheck } from "@whitespace/whitespace-utils";
import { useEffect, useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { useStateMachine } from "little-state-machine";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import AmplifyError from "components/AmplifyError/AmplifyError";
import regionSchema from "modules/schemas/regionSchema";
import "styles/form.scss";
import IEditRegionRequest from "interfaces/Request/IEditRegionRequest";
import ISaveRegionRequest from "interfaces/Request/ISaveRegionRequest";
import { TimePickerComponent } from "@syncfusion/ej2-react-calendars";
import { format } from "date-fns";

import "./EditRegion.scss";
import { RoutePathsEnum } from "infrastructure/RoutePathsEnum";

const EditRegion = () => {
  const { state } = useStateMachine();
  const [region, setRegion] = useState<IEditRegionRequest | undefined>(
    state.editRegionRequest
  );
  const [showExceptions, setShowExceptions] = useState<boolean>(false);
  const [saveButtonDisabled, setSaveButtonDisabled] = useState<boolean>(true);
  const {
    formState: { errors },
    register,
    handleSubmit,
    getValues,
    watch
  } = useForm<ISaveRegionRequest>({
    resolver: yupResolver(regionSchema),
    shouldFocusError: true,
    reValidateMode: "onBlur"
  });

  const navigate = useNavigate();

  const {
    isSending,
    errors: apiErrors,
    data,
    sendRequest,
    statusCode
  } = useAmplifyREST();

  const watchFields = watch(["active", "name"]);

  const [statusReportTimeOneRef, setStatusReportTimeOneRef] = useState<TimePickerComponent | undefined>();
  const [statusReportTimeTwoRef, setStatusReportTimeTwoRef] = useState<TimePickerComponent | undefined>();
  const [statusReportTimeThreeRef, setStatusReportTimeThreeRef] = useState<TimePickerComponent | undefined>();
  const [statusReportTimeFourRef, setStatusReportTimeFourRef] = useState<TimePickerComponent | undefined>();
  const [showDuplicateError, setShowDuplicateError] = useState(false);

  const numberOfStatusReportTimesPopulated = () : number => {
    let statusReportTimeGeneratedCounter = 0;

    if (statusReportTimeOneRef!.value) {
      statusReportTimeGeneratedCounter++;
    }

    if (statusReportTimeTwoRef!.value) {
      statusReportTimeGeneratedCounter++;
    }

    if (statusReportTimeThreeRef!.value) {
      statusReportTimeGeneratedCounter++;
    }

    if (statusReportTimeFourRef!.value) {
      statusReportTimeGeneratedCounter++;
    }

    return statusReportTimeGeneratedCounter;
  };

  const haveStatusReportTimesChanged = () => {
    // console.log(region!.statusReportTimes);
    if (!region!.statusReportTimes) {
      return true;
    }
    const times = region!.statusReportTimes.map(x => format(new Date(x), "HH:mm"));

    const timesChanged = !((!statusReportTimeOneRef!.value || times.includes(format(statusReportTimeOneRef!.value, "HH:mm")))
    && (!statusReportTimeTwoRef!.value || times.includes(format(statusReportTimeTwoRef!.value, "HH:mm")))
    && (!statusReportTimeThreeRef!.value || times.includes(format(statusReportTimeThreeRef!.value, "HH:mm")))
    && (!statusReportTimeFourRef!.value || times.includes(format(statusReportTimeFourRef!.value, "HH:mm"))));

    const sameNumberOfStatusReportTimesPopulatedAsOriginal = numberOfStatusReportTimesPopulated() === region!.statusReportTimes.length;

    return timesChanged || !sameNumberOfStatusReportTimesPopulatedAsOriginal;
  };

  const canFormBeSubmitted = () : boolean => {
    const nameUpdate = getValues("name") !== region?.name;
    const statusUpdate = getValues("active") !== region?.active;
    const canBeSubmitted = (nameUpdate || statusUpdate || haveStatusReportTimesChanged()) && numberOfStatusReportTimesPopulated() > 1;

    return canBeSubmitted;
  };

  useEffect(() => {
    if (region && statusReportTimeOneRef && statusReportTimeTwoRef && statusReportTimeThreeRef && statusReportTimeFourRef) {
      setSaveButtonDisabled(!canFormBeSubmitted());
    }
  }, [watchFields, region, statusReportTimeOneRef, statusReportTimeTwoRef, statusReportTimeThreeRef, statusReportTimeFourRef]);

  useEffect(() => {
    setRegion(state.editRegionRequest);
  }, [state.editRegionRequest]);

  const handleNavToSettings = () => {
    navigate(RoutePathsEnum.Settings);
  };

  const handleNavToRegions = () => {
    navigate(RoutePathsEnum.SettingsRegions);
  };

  // eslint-disable-next-line max-len
  const hasDuplicateStatusReportTimes = (enteredTimes : string[]) : boolean => enteredTimes.filter((currentValue, currentIndex) => enteredTimes.indexOf(currentValue) !== currentIndex).length !== 0;

  const handleSaveRegionClick = (editRegionRequest: ISaveRegionRequest) => {
    setShowDuplicateError(false);
    editRegionRequest.statusReportTimes = [];

    if (statusReportTimeOneRef!.value) {
      editRegionRequest.statusReportTimes.push(`1970-01-01T${format(statusReportTimeOneRef!.value, "HH:mm")}:00.000`);
    }

    if (statusReportTimeTwoRef!.value) {
      editRegionRequest.statusReportTimes.push(`1970-01-01T${format(statusReportTimeTwoRef!.value, "HH:mm")}:00.000`);
    }

    if (statusReportTimeThreeRef!.value) {
      editRegionRequest.statusReportTimes.push(`1970-01-01T${format(statusReportTimeThreeRef!.value, "HH:mm")}:00.000`);
    }

    if (statusReportTimeFourRef!.value) {
      editRegionRequest.statusReportTimes.push(`1970-01-01T${format(statusReportTimeFourRef!.value, "HH:mm")}:00.000`);
    }

    if (hasDuplicateStatusReportTimes(editRegionRequest.statusReportTimes)) {
      setShowDuplicateError(true);
      return;
    }

    sendRequest(`settings/regions/${region?.id}`, "PUT", editRegionRequest);
  };

  const exceptionAlertCloseHandler = useCallback(() => {
    setShowExceptions(false);
  }, [setShowExceptions]);

  useEffect(() => {
    if (data && statusCode === 200) handleNavToRegions();
  }, [data, statusCode]);

  useEffect(() => {
    if (statusCode === 401) navigate(RoutePathsEnum.Unauthorised);
  }, [statusCode]);

  useEffect(() => {
    if (apiErrors) setShowExceptions(true);
  }, [apiErrors]);

  const renderStatusReportTime = (idx:number) => (
    <Row className="mb-3 mt-3" key={`statusReportTime_${idx}`}>
      <div className="w-auto">
        <FormGroup
          as={Row}
          className="mb-6 d-flex w-50"
          controlId="active"
        >
          <Form.Label
            id={`reportTime${idx}Label`}
            className="d-inline-flex horiz-form-label w-auto"
          >
            Report
            {" "}
            {idx + 1}
            {" "}
            Time:
          </Form.Label>
          <div>
            <TimePickerComponent
              ref={(t: TimePickerComponent) => {
                switch (idx) {
                  case 0: {
                    setStatusReportTimeOneRef(t);
                    break;
                  }
                  case 1: {
                    setStatusReportTimeTwoRef(t);
                    break;
                  }
                  case 2: {
                    setStatusReportTimeThreeRef(t);
                    break;
                  }
                  case 3: {
                    setStatusReportTimeFourRef(t);
                    break;
                  }
                  default: {
                    break;
                  }
                }
              }}
              data-testid={`reportTime${idx}Label`}
              id={`reportTime${idx}`}
              type="text"
              htmlAttributes={{ "aria-labelledby": `reportTime${idx}Label` }}
              format="HH:mm"
              value={region?.statusReportTimes && region?.statusReportTimes[idx] ? region?.statusReportTimes[idx] : undefined}
              onChange={() => setSaveButtonDisabled(!canFormBeSubmitted())}
              showClearButton={false}
            />
          </div>
          <ErrorMessages
            fieldName={`reportTime${idx}`}
            errors={errors}
            validationFailures={null}
          />
        </FormGroup>
      </div>

    </Row>

  );

  return (
    <Page>
      {showExceptions && apiErrors && (
        <AmplifyError
          errors={apiErrors}
          isDismissable
          onCloseHandler={exceptionAlertCloseHandler}
        />
      )}
      <Divider />

      <Card>
        <Card.Header>
          <div className="float-start">
            <h2 data-testid="region-management">
              Edit Region
              <Spinner
                animation="grow"
                visible={isSending}
                spinnerText="Loading"
                variant="info"
              />
            </h2>
          </div>
        </Card.Header>
        <Card.Body className="no-padding">
          <Form
            autoComplete="off"
            onSubmit={handleSubmit(handleSaveRegionClick)}
          >
            <Row className="d-lg-flex w-100 align-items-center top-margin-small bottom-margin-small">
              <Col xs={8} className="d-flex">
                <Breadcrumb className="breadcrumb-manage-user left-margin-large no-wrap">
                  <Breadcrumb.Item
                    data-testid="breadcrumb-settings"
                    linkAs={Link}
                    linkProps={{ to: RoutePathsEnum.Home }}
                    onClick={handleNavToSettings}
                  >
                    Settings
                  </Breadcrumb.Item>
                  <Breadcrumb.Item
                    data-testid="breadcrumb-regions"
                    linkAs={Link}
                    linkProps={{ to: RoutePathsEnum.Home }}
                    onClick={handleNavToRegions}
                  >
                    Regions
                  </Breadcrumb.Item>
                  <Breadcrumb.Item active data-testid="breadcrumb-edit-region">
                    {region?.name}
                  </Breadcrumb.Item>
                </Breadcrumb>
              </Col>

              <Col className="d-lg-flex justify-content-end user-filter">
                <IconButton
                  iconType={IconButtonType.Save}
                  variant="primary"
                  className="right-margin-small"
                  disabled={saveButtonDisabled || isSending}
                  aria-disabled={saveButtonDisabled || isSending}
                  type="submit"
                  spin={isSending}
                  data-testid="save"
                  aria-label="save region"
                >
                  Update
                </IconButton>
                <Button
                  variant="secondary"
                  onClick={handleNavToRegions}
                  data-testid="cancel"
                >
                  Cancel
                </Button>
              </Col>
            </Row>

            <Divider />
            <section className="region pt-2">
              <Container fluid>
                <Row>
                  <div className="control-pane">
                    <div className="control-section" />

                    <Row className="mt-3">
                      <FormGroup as={Row} controlId="name">
                        <Form.Label id="nameLabel" className="horiz-form-label">
                          Region:
                        </Form.Label>
                        <Col>
                          <Form.Control
                            type="text"
                            role="textbox"
                            defaultValue={region?.name}
                            aria-required="true"
                            aria-labelledby="nameLabel"
                            aria-invalid={ariaInvalidCheck(
                              "name",
                              errors,
                              null
                            )}
                            maxLength={50}
                            minLength={1}
                            {...register("name")}
                          />
                        </Col>
                        <ErrorMessages
                          fieldName="name"
                          errors={errors}
                          validationFailures={null}
                        />
                      </FormGroup>
                    </Row>
                    <Row className="mb-3 mt-3">
                      <FormGroup
                        as={Row}
                        className="mb-6 d-flex"
                        controlId="active"
                      >
                        <Form.Label
                          id="activeLabel"
                          className="d-inline-flex horiz-form-label w-auto mb-0 me-3"
                        >
                          Active:
                        </Form.Label>

                        <Form.Check
                          type="switch"
                          className="d-inline-flex custom-switch custom-switch-md ms-2 w-auto"
                          aria-labelledby="activeLabel"
                          defaultChecked={region?.active}
                          aria-required="true"
                          {...register("active")}
                          data-testid="status"
                        />

                        <ErrorMessages
                          fieldName="active"
                          errors={errors}
                          validationFailures={null}
                        />
                      </FormGroup>
                    </Row>
                    {showDuplicateError && (
                      <span
                        data-testid="duplicateTimeEntered"
                        className="validation-error"
                      >
                        Duplicate Time entry. Cannot be saved

                      </span>
                    )}

                    <span className="status-report-times">
                      { renderStatusReportTime(0) }
                      { renderStatusReportTime(1) }
                      { renderStatusReportTime(2) }
                      { renderStatusReportTime(3) }
                    </span>

                  </div>
                </Row>
              </Container>
            </section>
          </Form>
        </Card.Body>
      </Card>
    </Page>
  );
};

export default EditRegion;
